/**
 * Chat-plane mutations: send a message, toggle a reaction, delete or edit
 * one's own message. Each publishes the Concord stream event and appends the
 * wrap to the local cache optimistically.
 */

import { useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNostr } from "@nostrify/react";
import type { NostrEvent } from "@nostrify/nostrify";

import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useToast } from "@/hooks/useToast";
import {
  publishChatMessage,
  publishDeleteMessage,
  publishEditMessage,
  publishReaction,
} from "@/lib/concord/ops";
import { communityRelays } from "@/hooks/useChama";
import type { Membership } from "@/lib/concord/types";

export function useChatActions(
  membership: Membership | undefined,
  channelId: string | undefined,
  appendWrap: (wrap: NostrEvent) => Promise<void>,
) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { toast } = useToast();

  const sink = useMemo(() => {
    const relays = communityRelays(membership);
    const group = nostr.group(relays);
    return { event: (e: NostrEvent) => group.event(e, { signal: AbortSignal.timeout(8000) }) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nostr, membership?.communityId]);

  const onError = (error: Error) => {
    console.error(error);
    toast({
      title: "Couldn't publish",
      description: "Your message didn't reach the community relays. Try again.",
      variant: "destructive",
    });
  };

  const send = useMutation({
    mutationFn: async (content: string) => {
      if (!user || !membership || !channelId) throw new Error("Not ready");
      const result = await publishChatMessage({
        signer: user.signer,
        authorPub: user.pubkey,
        membership,
        channelId,
        content,
        sink,
      });
      await appendWrap(result.wrap);
      return result;
    },
    onError,
  });

  const react = useMutation({
    mutationFn: async (opts: { targetId: string; targetPub: string; emoji: string }) => {
      if (!user || !membership || !channelId) throw new Error("Not ready");
      const result = await publishReaction({
        signer: user.signer,
        authorPub: user.pubkey,
        membership,
        channelId,
        ...opts,
        sink,
      });
      await appendWrap(result.wrap);
      return result;
    },
    onError,
  });

  const remove = useMutation({
    mutationFn: async (targetId: string) => {
      if (!user || !membership || !channelId) throw new Error("Not ready");
      const result = await publishDeleteMessage({
        signer: user.signer,
        authorPub: user.pubkey,
        membership,
        channelId,
        targetId,
        sink,
      });
      await appendWrap(result.wrap);
      return result;
    },
    onError,
  });

  const edit = useMutation({
    mutationFn: async (opts: { targetId: string; content: string }) => {
      if (!user || !membership || !channelId) throw new Error("Not ready");
      const result = await publishEditMessage({
        signer: user.signer,
        authorPub: user.pubkey,
        membership,
        channelId,
        ...opts,
        sink,
      });
      await appendWrap(result.wrap);
      return result;
    },
    onError,
  });

  return { send, react, remove, edit };
}
